import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { AlertTriangle, Ruler, Box, RefreshCw, Loader2, Download, Printer, Maximize2, Archive } from "lucide-react";
import { BACKEND, ImageLightbox, downloadUrl, printImage } from "./shared";

export function BuildingPlan({ reference }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [b3d, setB3d] = useState({ status: "none", has_image: false });
  const [bust, setBust] = useState(Date.now());
  const [gen, setGen] = useState(false);
  const [packing, setPacking] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/booking/${reference}/building-plan`);
        if (active) setPlan(r.data);
        const st = await api.get(`/admin/booking/${reference}/building-plan/render-3d/status`);
        if (active) { setB3d(st.data); setBust(Date.now()); }
      } catch { toast.error("Could not load the building plan"); }
      if (active) setLoading(false);
    })();
    return () => { active = false; if (pollRef.current) clearInterval(pollRef.current); };
  }, [reference]);

  const poll3d = useCallback(() => {
    let tries = 0;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const st = await api.get(`/admin/booking/${reference}/building-plan/render-3d/status`);
        if (st.data.status !== "generating" || tries > 30) {
          clearInterval(pollRef.current);
          setGen(false);
          setB3d(st.data);
          setBust(Date.now());
          if (st.data.status === "done") toast.success("3D build visual ready");
          else if (st.data.status === "error") toast.error("3D visual generation failed — try again");
        }
      } catch (e) { console.debug("3D status poll failed (will retry):", e); }
    }, 5000);
  }, [reference]);

  const generate3d = async () => {
    setGen(true);
    try {
      await api.post(`/admin/booking/${reference}/building-plan/render-3d`);
      setB3d((p) => ({ ...p, status: "generating" }));
      poll3d();
    } catch { setGen(false); toast.error("Could not start 3D generation"); }
  };

  const downloadPack = async () => {
    setPacking(true);
    try {
      const r = await api.get(`/admin/booking/${reference}/building-control-pack.zip`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Building-Control-Pack-${reference}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Building Control submission pack downloaded");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not build the submission pack";
      toast.error(typeof msg === "string" ? msg : "Could not build the submission pack");
    } finally {
      setPacking(false);
    }
  };

  if (loading) return <div data-testid="jobfile-building-plan" className="py-10 text-center text-sm text-[#6B7280]">Loading plan…</div>;
  if (!plan) return <div data-testid="jobfile-building-plan" />;
  if (!plan.available) {
    return (
      <div data-testid="jobfile-building-plan" className="py-10 text-center text-sm text-[#6B7280]">
        {plan.reason || "No configured design captured for this job, so a plan cannot be generated."}
      </div>
    );
  }

  const sketch = (kind) => `${BACKEND}/api/admin/booking/${reference}/plan-sketch/${kind}.png`;
  const img3d = `${BACKEND}/api/admin/booking/${reference}/building-plan/render-3d.png?ts=${bust}`;
  const generating = gen || b3d.status === "generating";
  const sm = plan.summary || {};
  const chips = [
    ["Width", `${sm.width_m} m`], ["Projection", `${sm.depth_m} m`], ["Floor area", `${sm.area_m2} m\u00b2`],
    ["Height", `${sm.height_m} m`], ["Roof", sm.roof], ["Use", sm.use],
  ];
  const drawings = [
    ["plan", "Proposed plan"],
    ["internal-layout", "Internal layout"],
    ["elevation", "Rear elevation"],
    ["section", "Cross-section"],
    ["floor-detail", "Suspended floor detail"],
    ["roof-detail", "Roof build-up detail"],
    ["isometric", "Construction isometric"],
  ];

  return (
    <div data-testid="jobfile-building-plan" className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow">Building Control plan</div>
          <h3 className="text-lg font-black text-[#12263A]">Technical specification &amp; schedule</h3>
          <p className="text-xs text-[#6B7280] mt-1 max-w-md">Indicative spec generated from the configured design — internal use, for pricing &amp; pre-application. Final structural calculations to be confirmed by a structural engineer.</p>
        </div>
        <button data-testid="building-control-pack-zip" onClick={downloadPack} disabled={packing}
          className="inline-flex items-center gap-1.5 bg-[#12263A] hover:bg-[#0B1929] disabled:opacity-60 text-white text-xs px-3 py-2 rounded-sm transition-colors">
          {packing ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />}
          {packing ? "Preparing pack…" : "Building Control submission pack (ZIP)"}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {chips.map(([k, v]) => (
          <div key={k} className="border border-[#E5E7EB] bg-white p-2.5">
            <div className="label-eyebrow">{k}</div>
            <div className="text-sm font-bold text-[#12263A]">{v}</div>
          </div>
        ))}
      </div>

      {/* AI 3D construction cut-away */}
      <section data-testid="building-plan-3d" className="border border-[#E5E7EB] bg-white">
        <div className="bg-[#12263A] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5"><Box size={13} /> 3D construction cut-away</span>
          <button data-testid="building-plan-3d-generate" onClick={generate3d} disabled={generating}
            className="inline-flex items-center gap-1.5 bg-[#C79A3B] hover:bg-[#A87E28] disabled:opacity-60 text-white px-2.5 py-1 text-[10px] rounded-sm transition-colors">
            {generating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {generating ? "Generating…" : (b3d.has_image ? "Regenerate" : "Generate 3D visual")}
          </button>
        </div>
        <div className="p-4">
          <p className="text-xs text-[#6B7280] mb-3">An AI-generated cut-away showing the build — strip foundation, suspended timber floor over a ventilated void, cavity walls and roof structure. Indicative only; ~1 minute to generate.</p>
          {b3d.has_image ? (
            <figure className="relative group max-w-2xl mx-auto">
              <img data-testid="building-plan-3d-image" src={img3d} alt="3D construction cut-away"
                onClick={() => setLightbox({ src: img3d, title: "3D construction cut-away", filename: `3D-cutaway-${reference}.png` })}
                className="w-full border border-[#E5E7EB] bg-[#F8F6F2] cursor-zoom-in" />
              <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button data-testid="building-plan-3d-enlarge" onClick={() => setLightbox({ src: img3d, title: "3D construction cut-away", filename: `3D-cutaway-${reference}.png` })} className="bg-white/95 border border-[#E5E7EB] p-1.5 hover:text-[#C79A3B]" title="Enlarge"><Maximize2 size={14} /></button>
                <button data-testid="building-plan-3d-download" onClick={() => downloadUrl(img3d, `3D-cutaway-${reference}.png`)} className="bg-white/95 border border-[#E5E7EB] p-1.5 hover:text-[#C79A3B]" title="Download"><Download size={14} /></button>
                <button data-testid="building-plan-3d-print" onClick={() => printImage(img3d, "3D construction cut-away")} className="bg-white/95 border border-[#E5E7EB] p-1.5 hover:text-[#C79A3B]" title="Print"><Printer size={14} /></button>
              </div>
            </figure>
          ) : (
            <div className="py-10 text-center text-sm text-[#9CA3AF] border border-dashed border-[#E5E7EB] bg-[#FAFAF8]">
              {generating ? "Generating your 3D build visual… this takes about a minute." : "No 3D visual yet — click \u201CGenerate 3D visual\u201D above."}
            </div>
          )}
        </div>
      </section>

      <div>
        <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Ruler size={13} /> Dimensioned &amp; construction drawings</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {drawings.map(([kind, label]) => {
            const src = sketch(kind);
            const fname = `${label.replace(/[^a-z0-9]+/gi, "-")}-${reference}.png`;
            return (
              <figure key={kind} className="relative group">
                <img data-testid={`building-plan-sketch-${kind}`} src={src} alt={label}
                  onClick={() => setLightbox({ src, title: label, filename: fname })}
                  className="w-full border border-[#E5E7EB] bg-white cursor-zoom-in" />
                <div className="absolute top-1.5 right-1.5 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`sketch-enlarge-${kind}`} onClick={() => setLightbox({ src, title: label, filename: fname })} className="bg-white/95 border border-[#E5E7EB] p-1.5 hover:text-[#C79A3B]" title="Enlarge"><Maximize2 size={13} /></button>
                  <button data-testid={`sketch-download-${kind}`} onClick={() => downloadUrl(src, fname)} className="bg-white/95 border border-[#E5E7EB] p-1.5 hover:text-[#C79A3B]" title="Download"><Download size={13} /></button>
                  <button data-testid={`sketch-print-${kind}`} onClick={() => printImage(src, label)} className="bg-white/95 border border-[#E5E7EB] p-1.5 hover:text-[#C79A3B]" title="Print"><Printer size={13} /></button>
                </div>
                <figcaption className="text-[11px] text-[#6B7280] mt-0.5">{label}</figcaption>
              </figure>
            );
          })}
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-1">Schematic — not to scale. Click any drawing to enlarge, download or print.</p>
      </div>

      {plan.sections.map((sec) => (
        <section key={sec.key} data-testid={`building-plan-section-${sec.key}`} className="border border-[#E5E7EB] bg-white">
          <div className="bg-[#12263A] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest">{sec.title}</div>
          <div className="p-4 space-y-3">
            {sec.intro && <p className="text-xs text-[#6B7280] italic">{sec.intro}</p>}
            {sec.items?.length > 0 && (
              <dl className="divide-y divide-[#F0F1F3]">
                {sec.items.map((it, i) => (
                  <div key={`${it.label}-${i}`} className="py-1.5 grid grid-cols-[120px_1fr] gap-3">
                    <dt className="text-xs font-bold text-[#12263A]">{it.label}</dt>
                    <dd className="text-xs text-[#374151]">{it.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {sec.table?.rows?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#F8F6F2] text-[#12263A]">
                      {sec.table.columns.map((c, i) => <th key={`${c}-${i}`} className="px-2.5 py-1.5 text-left font-bold">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {sec.table.rows.map((r, ri) => (
                      <tr key={`${r[0] || ""}-${ri}`} className="border-t border-[#E5E7EB]">
                        {r.map((cell, ci) => <td key={ci} className={`px-2.5 py-1.5 ${ci === 0 ? "font-semibold text-[#12263A]" : "text-[#374151]"}`}>{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {sec.notes?.length > 0 && (
              <ul className="list-disc pl-4 text-[11px] text-[#6B7280] space-y-0.5">
                {sec.notes.map((n, i) => <li key={`${i}-${n}`}>{n}</li>)}
              </ul>
            )}
          </div>
        </section>
      ))}

      <div data-testid="building-plan-disclaimer" className="border-l-4 border-[#C79A3B] bg-[#FBF3EC] p-3 flex gap-2">
        <AlertTriangle size={15} className="text-[#C79A3B] flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-[#6B4A12] leading-relaxed">{plan.disclaimer}</p>
      </div>

      {lightbox && <ImageLightbox {...lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
